import { type ChangeEvent, type DragEvent, useEffect, useState } from 'react';
import { Camera, CheckCircle, Upload, X } from 'lucide-react';

import { cn } from '@/lib/utils';

export type CardSide = 'front' | 'back';

interface InsuranceCardUploadDropzoneProps {
  accept?: string;
  file: File | null;
  isDragging: boolean;
  label: string;
  side: CardSide;
  error?: string | null;
  required?: boolean;
  onDragLeave: (event: DragEvent) => void;
  onDragOver: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
  onFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}

export function InsuranceCardUploadDropzone({
  accept = 'image/jpeg,image/png,image/heic',
  file,
  isDragging,
  label,
  side,
  error,
  required = false,
  onDragLeave,
  onDragOver,
  onDrop,
  onFileSelect,
  onRemove,
}: InsuranceCardUploadDropzoneProps) {
  const desktopInputId = `${side}-card-desktop-upload`;
  const mobileInputId = `${side}-card-mobile-upload`;

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <p className="text-charcoal-900 text-sm font-medium">
          {label}
          {!required && <span className="text-charcoal-500 font-normal"> (optional)</span>}
        </p>
        {file && <CheckCircle className="h-4 w-4 text-green-600" aria-label="Photo selected" />}
      </div>

      <div className="sm:hidden">
        <input
          id={mobileInputId}
          type="file"
          accept={accept}
          onChange={onFileSelect}
          className="sr-only"
          aria-label={`Upload ${label.toLowerCase()}`}
          aria-invalid={Boolean(error)}
        />
        {file ? (
          <SelectedImagePreview file={file} label={label} onRemove={onRemove} />
        ) : (
          <label
            htmlFor={mobileInputId}
            className="relative flex h-48 cursor-pointer flex-col items-center justify-center gap-3 overflow-hidden rounded-lg border-2 border-dashed border-gray-300 text-center transition-colors hover:bg-gray-50"
          >
            <div className="flex flex-col items-center gap-3 p-6">
              <Camera className="h-10 w-10 text-gray-400" />
              <p className="text-sm text-gray-500">
                <span className="text-hydrangea-600 font-medium">Take a photo</span> or upload
              </p>
            </div>
          </label>
        )}
        {/* No capture attribute: mobile browsers show their native Camera/Library/Files chooser. */}
      </div>

      <div className="hidden sm:block">
        <input
          id={desktopInputId}
          type="file"
          accept={accept}
          onChange={onFileSelect}
          className="sr-only"
          aria-label={`Upload ${label.toLowerCase()}`}
          aria-invalid={Boolean(error)}
        />
        {file ? (
          <SelectedImagePreview file={file} label={label} onRemove={onRemove} />
        ) : (
          <label
            htmlFor={desktopInputId}
            className={cn(
              'relative flex h-48 cursor-pointer flex-col items-center justify-center gap-3 overflow-hidden rounded-lg border-2 border-dashed text-center transition-colors',
              isDragging && 'border-blue-500 bg-blue-50',
              !isDragging && 'border-gray-300 hover:bg-gray-50'
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <div className="flex flex-col items-center gap-3 p-6">
              <Upload className="h-10 w-10 text-gray-400" />
              <p className="text-sm text-gray-500">
                Drag and drop or <span className="text-hydrangea-600 font-medium">upload</span>
              </p>
            </div>
          </label>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-rose-500">{error}</p>}
    </div>
  );
}

interface ImagePreviewProps {
  file: File;
  label: string;
  onRemove: () => void;
}

function SelectedImagePreview({ file, label, onRemove }: ImagePreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!previewUrl) {
    return null;
  }

  return (
    <div className="relative h-48 overflow-hidden rounded-lg border-2 border-dashed border-green-500">
      <img src={previewUrl} alt={`${label} preview`} className="h-full w-full object-cover" />
      <button
        type="button"
        onClick={onRemove}
        className="text-charcoal-900 absolute top-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md ring-2 ring-white transition-colors hover:text-rose-600"
        aria-label={`Remove ${label.toLowerCase()} photo`}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}
